import {
  Grid,
  Button,
  Checkbox as MuiCheckbox,
  FormControlLabel,
  Typography,
  MenuItem,
  Chip,
  Autocomplete,
  TextField as MuiTextField,
  Badge,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField as TF,
  Divider,
} from "@mui/material";
import { useField } from "react-final-form";
import { useIntl } from "react-intl";
import { useMutation } from "react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { isEqual } from "lodash-es";
import { Checkbox } from "@mb-pro-ui/components/form";

import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import KeyIcon from "@mui/icons-material/Key";

import { Company, UserDataExtended } from "../utils/types";
import { useApi } from "../utils/ApiProvider";
import useEditedStyles, { useEditedColor } from "../hooks/useEditedStyles";
import TitledCard from "../components/templates/basic/TitledCard";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { TextField } from "../components/form/TextField";
import { FormSkeleton } from "../components/FormSkeleton";
import { useLocale } from "../locales/LocaleProvider";
import { useSnackbar } from "notistack";
import { snackbarOptions } from "../components/snackbar";

const CompanyPermissionsField = ({ companies, disabled }: { companies: Company[]; disabled?: boolean }) => {
  const {
    input,
    meta: { dirty = false, initial = null },
  } = useField<number[] | null>("companies", { allowNull: true });
  const companyMap = useMemo(() => {
    const map = new Map<number, string>();
    companies.forEach(({ id, name }) => map.set(id, name));
    return map;
  }, [companies]);
  const autocompleteInputRef = useRef<HTMLDivElement | null>(null);

  const { formatMessage } = useIntl();
  const { background: editedBackground, hover: editedHover } = useEditedColor();
  const editedStyle = useEditedStyles();

  const callInputOnChange = useCallback(
    (value: number[] | null) => {
      input.onChange(
        ((value === null || value.length === 0) && initial === null) ||
          isEqual(
            (value ?? []).sort((a, b) => a - b),
            initial?.sort((a, b) => a - b)
          )
          ? initial
          : value
      );
    },
    [input, initial]
  );

  const onGlobalChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const newValue = checked ? [...(input.value ?? []), -1] : (input.value ?? []).filter((id) => id !== -1);
      callInputOnChange(newValue);
    },
    [callInputOnChange, input.value]
  );

  const deleteCompany = useCallback(
    (deleteId: number) => () => {
      const newValue = (input.value ?? []).filter((id) => id !== deleteId);
      callInputOnChange(newValue);
    },
    [callInputOnChange, input.value]
  );

  const addCompany = useCallback(
    (id: number) => () => {
      autocompleteInputRef.current?.blur();
      const newValue = [...(input.value ?? []), id];
      callInputOnChange(newValue);
    },
    [callInputOnChange, input.value]
  );

  const sortOptions = useCallback(
    ({ id: a }, { id: b }) => (!initial ? 0 : initial.includes(a) === initial.includes(b) ? 0 : initial.includes(a) ? -1 : 1),
    [initial]
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <MuiCheckbox
              size="small"
              onChange={onGlobalChange}
              checked={input.value?.includes(-1)}
              disabled={disabled}
              sx={[
                initial?.includes(-1) !== input.value?.includes(-1) &&
                  !(initial === null && !input.value?.includes(-1)) && {
                    backgroundColor: editedBackground,
                    "&:hover": { backgroundColor: editedBackground },
                  },
              ]}
            />
          }
          label={
            <Badge
              variant="dot"
              invisible={
                !dirty ||
                !input.value?.includes(-1) ||
                isEqual(initial, input.value) ||
                isEqual(
                  initial ?? [],
                  (input.value ?? []).filter((i) => i !== -1)
                )
              }
              sx={{ "& .MuiBadge-dot": { backgroundColor: editedBackground, right: -3 } }}
            >
              <Typography>{formatMessage({ defaultMessage: "Global company access" })}</Typography>
            </Badge>
          }
          labelPlacement="end"
          sx={[{ m: 0 }]}
        />
      </Grid>
      {!input.value?.includes(-1) && (
        <>
          <Grid item xs={12}>
            <Autocomplete
              disablePortal
              options={companies.filter(({ id }) => !input.value?.includes(id)).sort(sortOptions)}
              getOptionKey={(option: Company) => option.id}
              getOptionLabel={(option: Company) => option.name}
              noOptionsText={formatMessage({ defaultMessage: "No options" })}
              renderOption={(props, { id, name }) => (
                <MenuItem
                  {...props}
                  sx={[
                    { padding: 0 },
                    (initial?.includes(id) || false) && { backgroundColor: editedBackground, "&.MuiMenuItem-root:hover": { backgroundColor: editedHover } },
                  ]}
                  onClick={addCompany(id)}
                >
                  {name}
                </MenuItem>
              )}
              className={dirty && initial && initial.filter((id) => id !== -1 && !input.value?.includes(id)).length > 0 ? editedStyle.input : undefined}
              renderInput={(params) => (
                <MuiTextField
                  inputRef={autocompleteInputRef}
                  {...params}
                  label={formatMessage({ defaultMessage: "Add new company permission" })}
                  variant="filled"
                  sx={{ margin: 0 }}
                  size="small"
                />
              )}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} container spacing={0.5}>
            {input.value?.map &&
              input.value
                ?.filter((id) => id !== -1)
                .map((id) => (
                  <Grid item key={`map-chip-${id}`}>
                    <Chip
                      label={companyMap.get(id)}
                      onDelete={deleteCompany(id)}
                      color={initial?.includes(id) ? "primary" : undefined}
                      className={!initial?.includes(id) ? editedStyle.chip : undefined}
                    />
                  </Grid>
                ))}
          </Grid>
        </>
      )}
    </Grid>
  );
};

type UserSettingsSubpageProps = {
  isLoading: boolean;
};
export const UserSettingsSubpage = ({ isLoading }: UserSettingsSubpageProps) => {
  const [passwordDialogOpen, setPasswordDialogOpenOpen] = useState(false);
  const [notMatch, setNotMatch] = useState(false);

  const openNewPasswordDialog = useCallback(() => {
    setNotMatch(false);
    setPasswordDialogOpenOpen(true);
  }, []);
  const closeNewPasswordDialog = useCallback(() => setPasswordDialogOpenOpen(false), []);

  const { id } = useParams<{ id: string }>();
  const { formatMessage } = useIntl();
  const api = useApi();
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const isNew = useMemo(() => id === "new", [id]);

  // TODO: további kezelt hibák felsorolása!!!
  const { errorHandler: onError } = useErrorHandler<UserDataExtended>([
    [
      { status: 401, type: "user-admin" },
      () => formatMessage({ defaultMessage: "You have no rights for user management." }),
      () => {
        history.push("/");
      },
    ],
    [{ status: 404 }, () => formatMessage({ defaultMessage: "User is not found." }), () => history.push("/users")],
  ]);

  const companiesQuery = () => api<Company[]>("/companies");
  const { mutate: getCompanies, data: companies, isLoading: companiesLoading } = useMutation({ mutationFn: companiesQuery, onError });

  const changePasswordQuery = (password: string) =>
    api(`/users/${id}/change-password`, { method: "post", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
  const onChangePasswordSuccess = useCallback(() => {
    enqueueSnackbar(formatMessage({ defaultMessage: "Password changed." }), snackbarOptions({ variant: "success" }, { closeSnackbar }));
  }, [closeSnackbar, enqueueSnackbar, formatMessage]);
  const { mutate: changePassword } = useMutation({ mutationFn: changePasswordQuery, onError, onSuccess: onChangePasswordSuccess });

  useEffect(() => {
    getCompanies();
  }, [getCompanies]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} md={6} lg={4}>
          <TitledCard title={formatMessage({ defaultMessage: "User data" })} stretch={true}>
            <Grid container spacing={1} sx={{ p: 1 }}>
              {isLoading || companiesLoading ? (
                <>
                  <Grid item xs={12}>
                    <FormSkeleton variant="input" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="input" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="input" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="button" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="button" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      required={isNew}
                      disabled={!isNew}
                      label={formatMessage({ defaultMessage: "Username" })}
                      variant="filled"
                      size="small"
                      fullWidth
                      sx={{ m: 0, minWidth: 50, "& .MuiInputBase-input": { pt: 2.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="fullname"
                      required
                      label={formatMessage({ defaultMessage: "Name" })}
                      variant="filled"
                      size="small"
                      fullWidth
                      sx={{ m: 0, minWidth: 50 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="email"
                      required
                      label={formatMessage({ defaultMessage: "E-mail" })}
                      variant="filled"
                      size="small"
                      fullWidth
                      sx={{ m: 0, minWidth: 50 }}
                    />
                  </Grid>
                  {!isNew && (
                    <>
                      <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start" }}>
                        <PasswordResetButton />
                      </Grid>
                      <Grid item xs={12}>
                        <Button variant="text" color="warning" onClick={openNewPasswordDialog} startIcon={<KeyIcon />} sx={{ pl: 2 }}>
                          {formatMessage({ defaultMessage: "Change password" })}
                        </Button>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Checkbox name="active" label={formatMessage({ defaultMessage: "Active" })} size="small" labelSx={{ m: 0 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <CompanyPermissionsField companies={companies ?? []} />
                  </Grid>
                </>
              )}
            </Grid>
          </TitledCard>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TitledCard title={formatMessage({ defaultMessage: "Toolbox rights" })} stretch={true}>
            <Grid container spacing={1} sx={{ p: 1 }}>
              {isLoading || companiesLoading ? (
                <>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSkeleton variant="checkbox" />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Checkbox name="rights.firmware_update" label={formatMessage({ defaultMessage: "Firmware update" })} size="small" labelSx={{ m: 0 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Checkbox
                      name="rights.firmware_upload"
                      label={formatMessage({ defaultMessage: "Firmware upload (only for developers)" })}
                      size="small"
                      labelSx={{ m: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Checkbox name="rights.remote_files" label={formatMessage({ defaultMessage: "Device file management" })} size="small" labelSx={{ m: 0 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Checkbox
                      name="rights.global_remote_files"
                      label={formatMessage({ defaultMessage: "Global device files" })}
                      size="small"
                      labelSx={{ m: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Checkbox name="rights.user_admin" label={formatMessage({ defaultMessage: "User admin" })} size="small" labelSx={{ m: 0 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Checkbox
                      name="rights.template_helper"
                      label={formatMessage({ defaultMessage: "Template helper management" })}
                      size="small"
                      labelSx={{ m: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Checkbox name="rights.limited_user" label={formatMessage({ defaultMessage: "Limited access user" })} size="small" labelSx={{ m: 0 }} />
                  </Grid>
                </>
              )}
            </Grid>
          </TitledCard>
        </Grid>
      </Grid>

      <Dialog
        open={passwordDialogOpen}
        onClose={closeNewPasswordDialog}
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const formData = new FormData(event.currentTarget);
            const formJson = Object.fromEntries((formData as any).entries());
            const { password, passwordAgain } = formJson;
            if (password !== passwordAgain) {
              setNotMatch(true);
              return;
            }
            changePassword(password);
            closeNewPasswordDialog();
          },
          sx: {
            minWidth: { xs: 9 / 10, sm: 400 },
          },
        }}
      >
        <DialogTitle>{formatMessage({ defaultMessage: "Change password" })}</DialogTitle>
        <DialogContent>
          <TF
            autoFocus
            required
            margin="dense"
            id="password"
            name="password"
            type="password"
            label={formatMessage({ defaultMessage: "Password" })}
            fullWidth
            variant="standard"
            autoComplete="new-password"
          />
          <TF
            required
            margin="dense"
            id="passwordAgain"
            name="passwordAgain"
            type="password"
            label={formatMessage({ defaultMessage: "Password again" })}
            fullWidth
            variant="standard"
            autoComplete="new-password"
          />
          {notMatch && (
            <Typography variant="caption" color="error">
              {formatMessage({ defaultMessage: "Passwords are different." })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNewPasswordDialog} color="inherit">
            {formatMessage({ defaultMessage: "Cancel" })}
          </Button>
          <Button type="submit"> {formatMessage({ defaultMessage: "Change password" })}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const PasswordResetButton = () => {
  const [passwordEmailSent, setPasswordEmailSent] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);

  const openAlertDialog = useCallback(() => setAlertDialogOpen(true), []);
  const closeAlertDialog = useCallback(() => setAlertDialogOpen(false), []);

  const api = useApi();
  const { locale } = useLocale();
  const { formatMessage } = useIntl();

  const {
    meta: { initial: name },
  } = useField("name");
  const {
    meta: { initial: email },
  } = useField("email");

  const { errorHandler: onError } = useErrorHandler([]);

  const createPasswordResetQuery = () => api<void>("/password-reset", { method: "post", body: JSON.stringify({ name, email, lang: locale }) });
  const onPasswordResetSuccess = useCallback(() => {
    setPasswordEmailSent(true);
  }, []);
  const { mutate: createPasswordResetMutate, isLoading: passwordResetLoading } = useMutation({
    mutationFn: createPasswordResetQuery,
    onError,
    onSuccess: onPasswordResetSuccess,
  });

  const closeAlertAndPasswordResetEmail = useCallback(() => {
    closeAlertDialog();
    createPasswordResetMutate();
  }, [closeAlertDialog, createPasswordResetMutate]);

  return (
    <>
      <Button
        variant="text"
        color="warning"
        onClick={openAlertDialog}
        disabled={passwordResetLoading || passwordEmailSent}
        startIcon={<ContactMailOutlinedIcon />}
        sx={{ pl: 2 }}
      >
        {formatMessage({ defaultMessage: "Send password reset e-mail" })}
      </Button>
      {passwordEmailSent && (
        <Alert severity="success" sx={{ py: 0, px: 1, ml: 1 }}>
          {formatMessage({ defaultMessage: "E-mail sent." })}
        </Alert>
      )}

      <Dialog open={alertDialogOpen} onClose={closeAlertDialog} sx={{ ".MuiDialogTitle-root": { backgroundColor: (theme) => theme.palette.warning.main } }}>
        <DialogTitle>{formatMessage({ defaultMessage: "Sending a password change email" })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{formatMessage({ defaultMessage: "Are you sure about send a password change email?" })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeAlertDialog}>
            {formatMessage({ defaultMessage: "Cancel" })}
          </Button>
          <Button color="warning" onClick={closeAlertAndPasswordResetEmail}>
            {formatMessage({ defaultMessage: "Send password change email" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
